import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Paperclip, X, FileText } from 'lucide-react';
import type { EntityAttachment } from '@pcp/shared';

// Accepted client-side (mirrors the server ATTACH_EXTS allowlist). image/* lets
// mobile camera/photo pickers through; the server re-validates by extension.
const ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.bmp,.tiff,.tif,.svg,.pdf,.txt,.md,.log,.csv,.json,.docx,.xls,.xlsx,image/*';

const dropBase: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)',
  padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', cursor: 'pointer',
  border: '1px dashed var(--border-default, var(--border-subtle))', background: 'var(--bg-base)',
  color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', textAlign: 'center',
};
const chipStyle: CSSProperties = {
  position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2,
  width: 72, padding: 'var(--space-1)', borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
};
const thumbBox: CSSProperties = {
  width: 56, height: 56, borderRadius: 'var(--radius-sm)', overflow: 'hidden',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--bg-subtle)', color: 'var(--text-tertiary)',
};
const nameStyle: CSSProperties = {
  fontSize: '10px', color: 'var(--text-tertiary)', maxWidth: '100%',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

function RemoveBtn({ onClick, disabled, label }: { onClick: () => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
      style={{
        position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%',
        border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer', padding: 0,
      }}
    >
      <X size={11} />
    </button>
  );
}

/** Preview for a not-yet-uploaded File (object URL for images, icon otherwise). */
function StagedThumb({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file.type.startsWith('image/')) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url
    ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    : <FileText size={20} />;
}

/** Attachment editor used by the create + edit request forms. Presentational:
 *  the parent owns upload timing (staged → uploaded on create; immediate on edit)
 *  and deletion. Shows existing (uploaded) attachments + staged (pending) files
 *  as removable thumbnail chips, with a click/drag-drop picker. */
export function RequestAttachmentField({
  existing = [], staged = [], onAddFiles, onRemoveStaged, onRemoveExisting, uploading, disabled,
}: {
  existing?: EntityAttachment[];
  staged?: File[];
  onAddFiles: (files: File[]) => void;
  onRemoveStaged?: (index: number) => void;
  onRemoveExisting?: (att: EntityAttachment) => void;
  uploading?: boolean;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const add = (list: FileList | null) => {
    if (!list || !list.length) return;
    onAddFiles(Array.from(list));
  };

  const hasAny = existing.length > 0 || staged.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {hasAny && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {existing.map((a) => (
            <div key={a.downloadUrl} style={chipStyle}>
              <span style={thumbBox}>
                {a.isImage
                  ? <img src={`${a.downloadUrl}&w=128`} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <FileText size={20} />}
              </span>
              <span style={nameStyle} title={a.name}>{a.name}</span>
              {onRemoveExisting && <RemoveBtn label={`Remove ${a.name}`} disabled={disabled} onClick={() => onRemoveExisting(a)} />}
            </div>
          ))}
          {staged.map((f, i) => (
            <div key={`${f.name}-${i}`} style={chipStyle}>
              <span style={thumbBox}><StagedThumb file={f} /></span>
              <span style={nameStyle} title={f.name}>{f.name}</span>
              {onRemoveStaged && <RemoveBtn label={`Remove ${f.name}`} disabled={disabled} onClick={() => onRemoveStaged(i)} />}
            </div>
          ))}
        </div>
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); inputRef.current?.click(); } }}
        onDragOver={(e) => { if (!disabled) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!disabled) add(e.dataTransfer.files); }}
        style={{ ...dropBase, opacity: disabled ? 0.6 : 1, cursor: disabled ? 'default' : 'pointer', background: dragOver ? 'var(--accent-primary-soft)' : dropBase.background, borderColor: dragOver ? 'var(--accent-primary)' : (dropBase.border as string) }}
      >
        <Paperclip size={15} />
        <span>{uploading ? 'Uploading…' : 'Add screenshots or files — click or drop here'}</span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        onChange={(e) => { add(e.target.files); e.target.value = ''; }}
        style={{ display: 'none' }}
      />
    </div>
  );
}
